import './App.css';
import './VerificationStyles.css';
import { API } from 'aws-amplify';
import React, { useState, useEffect } from "react";
import { ThemeProvider } from '@aws-amplify/ui-react';
import '@aws-amplify/ui-react/styles.css';
import { BrowserRouter } from "react-router-dom";
import Routes from "./Components/Routes";

const THEME_STORAGE_KEY = 'gi-theme-preference';

// Configure REST API for IDENNTITY verification backend
API.configure({
    API: {
        endpoints: [
            {
                name: "identityverification",
                endpoint: process.env.REACT_APP_ENV_API_URL || "http://localhost:3000"
            },
        ],
    },
});

function getInitialThemeState() {
    try {
        const saved = localStorage.getItem(THEME_STORAGE_KEY);
        if (saved === 'light' || saved === 'dark') {
            return { mode: saved, isUserSelected: true };
        }
    } catch (_) {
        // Ignore storage errors
    }
    const prefersDark =
        typeof window !== 'undefined' &&
        window.matchMedia &&
        window.matchMedia('(prefers-color-scheme: dark)').matches;
    return { mode: prefersDark ? 'dark' : 'light', isUserSelected: false };
}

function App() {
    const [{ mode: themeMode, isUserSelected }, setThemeState] = useState(getInitialThemeState);
    const isDark = themeMode === 'dark';

    // Apply data-theme attribute to <html> for CSS custom properties
    useEffect(() => {
        document.documentElement.setAttribute('data-theme', themeMode);
        document.documentElement.style.colorScheme = themeMode;
    }, [themeMode]);

    // Follow system color scheme changes until user explicitly switches
    useEffect(() => {
        if (isUserSelected || typeof window === 'undefined' || !window.matchMedia) {
            return;
        }
        const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
        const handleChange = (e) => {
            setThemeState((prev) =>
                prev.isUserSelected ? prev : { mode: e.matches ? 'dark' : 'light', isUserSelected: false }
            );
        };
        if (mediaQuery.addEventListener) {
            mediaQuery.addEventListener('change', handleChange);
            return () => mediaQuery.removeEventListener('change', handleChange);
        } else if (mediaQuery.addListener) {
            mediaQuery.addListener(handleChange);
            return () => mediaQuery.removeListener(handleChange);
        }
    }, [isUserSelected]);

    const handleToggleTheme = () => {
        const nextMode = isDark ? 'light' : 'dark';
        setThemeState({ mode: nextMode, isUserSelected: true });
        try {
            localStorage.setItem(THEME_STORAGE_KEY, nextMode);
        } catch (_) {
            // Ignore storage errors
        }
    };

    const theme = {
        name: 'giant-identity-theme',
        tokens: {
            colors: {
                brand: {
                    primary: {
                        10: isDark ? '#27272a' : '#FFFBEB',
                        20: isDark ? '#3f3f46' : '#FEF3C7',
                        40: '#FDE68A',
                        60: '#FCD34D',
                        80: '#fbbf24',
                        90: '#f59e0b',
                        100: '#d97706',
                    },
                },
                background: {
                    primary: { value: isDark ? '#18181b' : '#FFFFFF' },
                    secondary: { value: isDark ? '#09090b' : '#FFFDF5' }
                },
                font: {
                    primary: { value: isDark ? '#f4f4f5' : '#1E1B4B' },
                    secondary: { value: isDark ? '#a1a1aa' : '#6B7280' }
                }
            },
            fonts: {
                default: {
                    variable: { value: "'Figtree', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif" },
                    static: { value: "'Figtree', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif" }
                }
            },
            components: {
                button: {
                    primary: {
                        backgroundColor: { value: '#fbbf24' },
                        color: { value: '#0c0e15' },
                        _hover: {
                            backgroundColor: { value: '#f59e0b' }
                        }
                    }
                }
            }
        }
    };

    return (
        <ThemeProvider theme={theme} colorMode={themeMode}>
            <BrowserRouter>
                <div className="gi-page-container">
                    <button
                        type="button"
                        className="gi-theme-toggle"
                        onClick={handleToggleTheme}
                        aria-label={isDark ? "Switch to light mode" : "Switch to dark mode"}
                        title={isDark ? "Switch to light mode" : "Switch to dark mode"}
                    >
                        {isDark ? (
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <circle cx="12" cy="12" r="5" />
                                <line x1="12" y1="1" x2="12" y2="3" />
                                <line x1="12" y1="21" x2="12" y2="23" />
                                <line x1="4.22" y1="4.22" x2="5.64" y2="5.64" />
                                <line x1="18.36" y1="18.36" x2="19.78" y2="19.78" />
                                <line x1="1" y1="12" x2="3" y2="12" />
                                <line x1="21" y1="12" x2="23" y2="12" />
                                <line x1="4.22" y1="19.78" x2="5.64" y2="18.36" />
                                <line x1="18.36" y1="5.64" x2="19.78" y2="4.22" />
                            </svg>
                        ) : (
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
                            </svg>
                        )}
                    </button>
                    <Routes />
                </div>
            </BrowserRouter>
        </ThemeProvider>
    );
}

export default App;
